import { create } from "zustand";
import {
  connectModalEvidenceSocket,
  disconnectModalEvidenceSocket,
  getModalEvidenceSocket,
  subscribeModalEvidence,
  unsubscribeModalEvidence,
} from "../../evidenceModalSocket";
import { hasAnyFilter } from "../../evidenceSocket";
import type { EvidenceEvent, EvidenceFilter } from "../../evidenceSocket";
import type { EvidenceItem } from "./evidenceSocketStore";

interface EvidenceModalSocketStore {
  evidences: EvidenceItem[];
  isConnected: boolean;
  connecting: boolean;
  error: string | null;
  currentFilter: EvidenceFilter | null;
  joinedRooms: string[];
  maxBuffer: number;

  connect: (filter: EvidenceFilter) => void;
  disconnect: () => void;
  updateSubscription: (filter: EvidenceFilter) => void;
  addEvidence: (ev: EvidenceEvent) => void;
  clearEvidences: () => void;
}

let localIdCounter = 0;
let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
let reconnectAttempts = 0;
let intentionalClose = false;

const MODAL_MAX_BUFFER = 300;
const MAX_RECONNECT_ATTEMPTS = 10;
const RECONNECT_BASE_DELAY = 1000;
const RECONNECT_MAX_DELAY = 5000;

const nextLocalId = (): string => {
  localIdCounter += 1;
  return `modal-${Date.now()}-${localIdCounter}`;
};

export const useEvidenceModalSocketStore = create<EvidenceModalSocketStore>(
  (set, get) => ({
    evidences: [],
    isConnected: false,
    connecting: false,
    error: null,
    currentFilter: null,
    joinedRooms: [],
    maxBuffer: MODAL_MAX_BUFFER,

    connect: (filter: EvidenceFilter) => {
      intentionalClose = false;
      if (reconnectTimer) {
        clearTimeout(reconnectTimer);
        reconnectTimer = null;
      }
      set({ currentFilter: filter, connecting: true });

      try {
        const socket = connectModalEvidenceSocket();
        socket.removeAllListeners();

        const subscribe = () => {
          const activeFilter = get().currentFilter;
          if (activeFilter && hasAnyFilter(activeFilter)) {
            subscribeModalEvidence(activeFilter);
          }
        };

        const scheduleReconnect = () => {
          if (intentionalClose || reconnectTimer) return;
          if (reconnectAttempts >= MAX_RECONNECT_ATTEMPTS) {
            set({
              connecting: false,
              isConnected: false,
              error: "No se pudo reconectar",
            });
            return;
          }
          const delay = Math.min(
            RECONNECT_BASE_DELAY * 2 ** reconnectAttempts,
            RECONNECT_MAX_DELAY
          );
          set({ connecting: true });
          reconnectTimer = setTimeout(() => {
            reconnectTimer = null;
            reconnectAttempts += 1;
            disconnectModalEvidenceSocket();
            getModalEvidenceSocket().removeAllListeners();
            get().connect(get().currentFilter ?? filter);
          }, delay);
        };

        if (socket.connected) {
          reconnectAttempts = 0;
          set({ isConnected: true, connecting: false, error: null });
          subscribe();
        }

        socket.on("connect", () => {
          reconnectAttempts = 0;
          set({ isConnected: true, connecting: false, error: null });
          subscribe();
        });

        socket.on("disconnect", () => {
          set({ isConnected: false });
          if (intentionalClose) return;
          scheduleReconnect();
        });

        socket.on("connect_error", (err) => {
          set({ error: err.message, isConnected: false });
          if (intentionalClose) return;
          scheduleReconnect();
        });

        socket.on("subscribed:evidence", (response) => {
          set({ joinedRooms: response?.rooms ?? [] });
        });

        socket.on("evidence", (ev) => {
          get().addEvidence(ev);
        });
      } catch (error: unknown) {
        const message =
          error instanceof Error ? error.message : "Error de conexión";
        set({ error: message });
      }
    },

    disconnect: () => {
      intentionalClose = true;
      reconnectAttempts = 0;
      if (reconnectTimer) {
        clearTimeout(reconnectTimer);
        reconnectTimer = null;
      }

      const { currentFilter } = get();
      if (currentFilter && hasAnyFilter(currentFilter)) {
        unsubscribeModalEvidence(currentFilter);
      }

      const socket = getModalEvidenceSocket();
      socket.removeAllListeners();

      disconnectModalEvidenceSocket();
      set({
        isConnected: false,
        connecting: false,
        joinedRooms: [],
        evidences: [],
        currentFilter: null,
      });
    },

    updateSubscription: (filter: EvidenceFilter) => {
      const { currentFilter, isConnected } = get();

      if (isConnected && currentFilter && hasAnyFilter(currentFilter)) {
        unsubscribeModalEvidence(currentFilter);
      }

      set({ currentFilter: filter });

      if (isConnected && hasAnyFilter(filter)) {
        subscribeModalEvidence(filter);
      }
    },

    addEvidence: (ev: EvidenceEvent) => {
      set((state) => {
        const item: EvidenceItem = {
          ...ev,
          _localId: nextLocalId(),
          _receivedAt: Date.now(),
        };
        const next = [item, ...state.evidences];
        if (next.length > state.maxBuffer) {
          next.length = state.maxBuffer;
        }
        return { evidences: next };
      });
    },

    clearEvidences: () => set({ evidences: [] }),
  })
);
